// LandingPage v2.5.700 — hero + beta pill + ROI widget + PDF lead capture (2026-07-05 sync-force)
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import SmartVideo from '../components/SmartVideo';
import BusinessCarousel from '../components/BusinessCarousel';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  Check, Share2, Globe, Star, PenTool, Search, Shield, BarChart2, Megaphone, Mail,
  ArrowRight, Play, X, Clock, DollarSign, Brain, Zap, TrendingUp, Users,
  ChevronRight, AlertTriangle, Target, Layers, Award, Briefcase, Truck,
  CreditCard, ShoppingCart, Headphones, Building2, Puzzle, Database,
  FileText, Workflow, Settings, Lock, Shuffle, AlertCircle, TrendingDown, MapPin, Sparkles,
  Menu as MenuIcon
} from 'lucide-react';
import {
  SiStripe, SiGoogleads, SiMeta, SiQuickbooks, SiZoho,
  SiGoogledrive, SiWhatsapp, SiGmail
} from 'react-icons/si';
import { Briefcase as LinkedinFallback, Hash as SlackFallback } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// LinkedIn + Slack are not exported by react-icons/si (their brand policies
// require licensed usage). We use neutral Lucide fallbacks tinted with each
// brand's official color, and the trademark disclaimer below the row makes
// the attribution explicit.

// Brand-icon lookup for the integrations strip.
const INTEGRATION_ICON_MAP = {
  stripe: SiStripe,
  googleads: SiGoogleads,
  meta: SiMeta,
  linkedin: LinkedinFallback,
  quickbooks: SiQuickbooks,
  zoho: SiZoho,
  googledrive: SiGoogledrive,
  slack: SlackFallback,
  whatsapp: SiWhatsapp,
  gmail: SiGmail,
};

// Icon map for CMS-driven pain points (backend sends string names)
const PAIN_ICON_MAP = {
  shuffle: Shuffle,
  clock: Clock,
  'alert-circle': AlertCircle,
  database: Database,
  'trending-down': TrendingDown,
  puzzle: Puzzle,
  'dollar-sign': DollarSign,
  'alert-triangle': AlertTriangle,
};

// Compact hook: counts up to `target` over `duration` ms once triggered.
// Restarts whenever `trigger` changes so we can retrigger on scroll-into-view.
const useAnimatedCount = (target, duration = 1400, trigger = true) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let raf;
    const start = performance.now();
    const step = (t) => {
      const elapsed = t - start;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, trigger]);
  return value;
};

// Tiny in-view detector so counters trigger only when the ROI widget is visible
const useInView = (ref, options = { threshold: 0.3 }) => {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || inView) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, options);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref, inView, options]);
  return inView;
};

const features = [
  { icon: Target, title: 'Lead Generation & CRM', desc: 'Capture, score, and nurture leads through AI-powered pipelines that convert', stat: '3x conversions' },
  { icon: Briefcase, title: 'Sales & Proposals', desc: 'Digital proposals, invoicing, and pipeline management in one flow', stat: 'Close 40% faster' },
  { icon: Share2, title: 'Marketing & Content', desc: 'Social media, SEO, email campaigns, and AI content generation on autopilot', stat: '10x output' },
  { icon: Headphones, title: 'Customer Service', desc: 'Helpdesk, SLA tracking, and AI-powered ticket resolution', stat: '90% SLA rate' },
  { icon: Users, title: 'Customer Retention', desc: 'Loyalty programs, win-back campaigns, and churn prediction powered by AI', stat: '-45% churn' },
  { icon: Truck, title: 'Operations', desc: 'Task management, dispatch scheduling, announcements, and KPI dashboards', stat: 'Full visibility' },
  { icon: BarChart2, title: 'Analytics & Intelligence', desc: 'Unified command center with AI-driven smart alerts and automated reports', stat: 'Real-time' },
  { icon: Globe, title: 'Website & SEO', desc: 'Website management, ADA compliance, heatmaps, landing pages, and CRO', stat: '300% growth' },
  { icon: CreditCard, title: 'Payments & Commerce', desc: 'Stripe integration, invoicing, whitelabel billing, and multi-currency support', stat: 'Global payments' },
];

const painPoints = [
  { icon: Puzzle, title: 'Too many disconnected tools', desc: "Your CRM doesn't talk to your helpdesk. Your marketing runs separately from sales. You're paying for 10+ tools that don't share data — and losing insights in the gaps." },
  { icon: DollarSign, title: 'Enterprise software costs a fortune', desc: 'Salesforce, HubSpot, Zendesk, Mailchimp — the stack adds up to $10,000-$50,000/month. And you still need consultants to make them work together.' },
  { icon: AlertTriangle, title: 'Scaling breaks everything', desc: "What works for 10 employees falls apart at 100. New branches, new countries, new regulations — your current tools can't keep up with real growth." },
  { icon: Clock, title: "Your team is drowning in manual work", desc: 'Data entry, report generation, follow-up emails, ticket routing — your people spend 60% of their time on tasks AI can handle in seconds.' },
];

const solutions = [
  { before: '10+ disconnected tools and dashboards', after: '1 unified platform — everything connected', icon: Layers },
  { before: '$10,000-$50,000/month on software stack', after: 'Starting at $299/month, all-inclusive', icon: DollarSign },
  { before: 'Manual data entry and siloed departments', after: 'AI automation across every business function', icon: Brain },
  { before: 'Scaling breaks your processes', after: 'Enterprise-grade infrastructure that grows with you', icon: TrendingUp },
  { before: 'Months of migration and setup', after: 'Guided onboarding with full data migration support', icon: Zap },
];

const plans = [
  {
    name: 'Starter',
    price: '$299',
    period: '/month',
    desc: 'For small businesses getting organized',
    features: ['CRM & Lead Pipeline', 'Basic Analytics Dashboard', 'AI Content (100/mo)', 'Email & Chat Support', 'Website Monitoring', '5 Team Members'],
    cta: 'Start Free Trial'
  },
  {
    name: 'Growth',
    price: '$699',
    period: '/month',
    popular: true,
    desc: 'For scaling businesses that need more power',
    features: ['Everything in Starter', 'Advanced Analytics & Reports', 'AI Content (500/mo)', 'Priority Support', 'Full Marketing Suite', 'Helpdesk & SLA Tracking', 'Operations Module', '25 Team Members'],
    cta: 'Start Free Trial'
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'For large organizations & franchises',
    features: ['Everything in Growth', 'Unlimited AI & Team Members', 'White-Label & Multi-Tenant', 'Custom Integrations (Zoho, QuickBooks, etc.)', 'Full Data Migration Support', 'Dedicated Account Manager', 'API Access & Webhooks', 'Data Export & Cloud Backup', 'Custom Compliance & Security'],
    cta: 'Contact Sales'
  },
];

const testimonials = [
  {
    quote: "Konnect replaced 8 separate tools for us. Our teams finally see the same data — leads flow from marketing to sales to service without anyone re-entering anything.",
    name: "Mina Ashworth",
    role: "Head of Marketing, Holly Companies",
    rating: 5,
  },
  {
    quote: "Our day to day operations have never been this organized! The KonnectMeet tool is promising once it's polished, recording everything we have discussed via phone, transcribing it and instantly send minutes of the meeting is really great!",
    name: "Junnel Felix",
    role: "Operations, IL Mercato",
    rating: 5,
  },
  {
    quote: "HR & Operations modules are really great! I also like that based on data gathered throughout the usage of the SaaS, the platform, through Business Insights, can actually give you analysis of your business performance, show trends, and recommendations.",
    name: "Orange del Rosario",
    role: "Operations Head, Ceraqua",
    rating: 5,
  },
  {
    quote: "It excites me to know that aside from the 89 modules already available in Enterprise Tier, it will just grow and grow as subscribers suggest different use cases, also integration with external tools are also there. I am operating a vending company and I am glad that we can have a vending module as a special project or collaboration with Konnect.",
    name: "Gilbert Dela Cruz",
    role: "President & CEO, Vend",
    rating: 5,
  },
];

const stats = [
  { value: '10,000+', label: 'Businesses Automated' },
  { value: '45+', label: 'Countries & Currencies' },
  { value: '91%', label: 'Avg. Retention Rate' },
  { value: '60%', label: 'Less Manual Work' },
];

const integrationLogos = [
  { key: 'stripe',       label: 'Stripe',      color: '#635bff' },
  { key: 'googleads',    label: 'Google Ads',  color: '#4285F4' },
  { key: 'meta',         label: 'Meta',        color: '#0866FF' },
  { key: 'linkedin',     label: 'LinkedIn',    color: '#0A66C2' },
  { key: 'quickbooks',   label: 'QuickBooks',  color: '#2CA01C' },
  { key: 'zoho',         label: 'Zoho',        color: '#E42527' },
  { key: 'googledrive',  label: 'Google Drive',color: '#1FA463' },
  { key: 'slack',        label: 'Slack',       color: '#4A154B' },
  { key: 'whatsapp',     label: 'WhatsApp',    color: '#25D366' },
  { key: 'gmail',        label: 'Gmail',       color: '#EA4335' },
];

const enterpriseFeatures = [
  { icon: Building2, title: 'Multi-Tenant Architecture', desc: 'White-label for country operators, franchises, and holding companies' },
  { icon: Database, title: 'Full Data Migration', desc: 'We migrate your data from any legacy system — CRM, ERP, helpdesk, or spreadsheets' },
  { icon: Globe, title: 'Global Localization', desc: '155 currencies, 91 timezones, 60 languages — true global operations from day one' },
  { icon: Lock, title: 'Enterprise Security', desc: 'Role-based access, audit logs, data encryption, and compliance-ready infrastructure' },
  { icon: Workflow, title: 'Custom Integrations', desc: 'Connect to Zoho, QuickBooks, SAP, payment processors, and any REST API' },
  { icon: FileText, title: 'Data Export & Backup', desc: 'Download your data anytime. Sync to Google Drive or your own cloud for redundancy' },
];

export const LandingPage = () => {
  const [showDemoVideo, setShowDemoVideo] = useState(false);
  const [demoVideoUrl, setDemoVideoUrl] = useState('');
  const [investorFormOpen, setInvestorFormOpen] = useState(false);
  // CMS-driven landing content (managed via /settings/landing-editor by super_admin).
  // Falls back to backend DEFAULTS if no override, and to safe defaults if the
  // endpoint fails so the marketing page always renders.
  const [content, setContent] = useState(null);
  // v2.6.1 — Investor video slot can be sourced either from CMS
  // (content.investor.video_url) OR from a Media Management asset assigned to
  // `landing_investor_video`. The latter takes priority — see effect below.
  const [investorMedia, setInvestorMedia] = useState(null);
  // Whitelabel resolution — drives whether to redirect (enterprise tenant) or
  // render the landing with operator overrides (country_operator tenant).
  const [whitelabelLoading, setWhitelabelLoading] = useState(true);
  const [operatorBrand, setOperatorBrand] = useState(null);
  const navigate = useNavigate();

  // Pull CMS content + investor video once on mount. No auth needed (public endpoints).
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/public/landing-content`)
      .then((r) => { if (alive) setContent(r.data); })
      .catch(() => { /* fall back to inline defaults below */ });
    axios.get(`${API}/api/media/public/landing-investor-video`)
      .then((r) => { if (alive && r.data?.url) setInvestorMedia(r.data); })
      .catch(() => { /* no investor media assigned — CMS URL or placeholder shown */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const host = (typeof window !== 'undefined' && window.location.hostname) || '';
    // Apex / dev / preview hosts → never run the whitelabel check, always show normal landing.
    // We detect preview/dev environments generically (no vendor names) so the production
    // code stays brand-pure: Konnect-only.
    const isPreviewHost = host.includes('preview.') || host.includes('cluster-')
      || host === 'localhost' || host.startsWith('127.') || host.endsWith('.local');
    const isApex = host === 'konnect.kaharian.com' || host === 'www.konnect.kaharian.com' || isPreviewHost;
    if (isApex) {
      setWhitelabelLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/whitelabel/public/by-host?host=${encodeURIComponent(host)}`);
        if (!alive) return;
        if (data?.matched && data?.brand) {
          const role = (data.brand.owner_role || 'enterprise').toLowerCase();
          if (role === 'country_operator') {
            // Operators sell Konnect → show landing, but with their hero/pricing overrides.
            setOperatorBrand(data.brand);
            setWhitelabelLoading(false);
          } else {
            // Enterprise subscriber → root URL goes straight to login. They are
            // not in the business of selling Konnect to other companies.
            navigate('/login', { replace: true });
            return;
          }
        } else {
          // Subdomain not claimed → safest default is to send to login (avoids
          // exposing the marketing page on a random unclaimed subdomain).
          navigate('/login', { replace: true });
          return;
        }
      } catch {
        // On error, fall back to normal landing rather than infinite redirect.
        if (alive) setWhitelabelLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  // Fetch landing video URL when dialog opens
  const handleShowDemo = async () => {
    setShowDemoVideo(true);
    try {
      const { data } = await axios.get(`${API}/api/media/public/landing-video`);
      setDemoVideoUrl(data.url || '');
    } catch { setDemoVideoUrl(''); }
  };

  // Render guard: while we're resolving the host, don't flash the marketing
  // landing (would briefly show before the redirect for enterprise tenants).
  if (whitelabelLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="h-8 w-8 border-2 border-emerald-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Operator overrides: hero text + per-plan currency/price.
  const heroTitle = operatorBrand?.landing_hero_title;
  const heroSubtitle = operatorBrand?.landing_hero_subtitle;
  const currencySymbol = operatorBrand?.landing_currency_symbol || '$';
  const operatorPrices = {
    Starter: operatorBrand?.landing_starter_price,
    Growth: operatorBrand?.landing_growth_price,
    Enterprise: operatorBrand?.landing_enterprise_price,
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Investor enquiry modal */}
      {investorFormOpen && (
        <InvestorEnquiryModal onClose={() => setInvestorFormOpen(false)} />
      )}
      {/* Header — v2.6.16
          Uses `fixed` instead of `sticky` because the root <div> has
          overflow-x-hidden which silently breaks position:sticky (browser
          treats overflow-hidden ancestor as a new scroll container).
          A same-height <div> spacer below reserves the layout height so
          the hero doesn't jump underneath it, EXCEPT on mobile where the
          hero image is designed to float behind the header (see -mt-16
          on the hero section). */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm" data-testid="landing-header">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-6 md:gap-8">
            {/* Mobile hamburger (opens the same nav as desktop, in a Sheet drawer) */}
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden h-9 w-9 -ml-1"
                  aria-label="Open menu"
                  data-testid="landing-mobile-menu-btn"
                >
                  <MenuIcon className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-72 sm:w-80 border-r-0 shadow-2xl"
                data-testid="landing-mobile-drawer"
              >
                <SheetHeader className="text-left mb-4">
                  <SheetTitle className="flex items-center gap-2">
                    <img src="/konnect-logo.png" alt="Konnect" className="h-6 w-auto object-contain" />
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-1 text-base">
                  <a href="#pain" className="rounded-lg px-3 py-2.5 text-text-primary hover:bg-gray-50 transition-colors" data-testid="mobile-nav-why">Why Konnect</a>
                  <a href="#features" className="rounded-lg px-3 py-2.5 text-text-primary hover:bg-gray-50 transition-colors" data-testid="mobile-nav-platform">Platform</a>
                  <a href="#enterprise" className="rounded-lg px-3 py-2.5 text-text-primary hover:bg-gray-50 transition-colors" data-testid="mobile-nav-enterprise">Enterprise</a>
                  <a href="#pricing" className="rounded-lg px-3 py-2.5 text-text-primary hover:bg-gray-50 transition-colors" data-testid="mobile-nav-pricing">Pricing</a>
                  <Link to="/public/marketplace" className="rounded-lg px-3 py-2.5 text-emerald-brand font-medium hover:bg-emerald-50 transition-colors" data-testid="mobile-nav-marketplace">Marketplace</Link>
                </nav>
                <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col gap-2">
                  <Link to="/login">
                    <Button variant="ghost" className="w-full justify-start text-text-primary" data-testid="mobile-nav-signin">Sign In</Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="w-full bg-emerald-brand hover:bg-emerald-hover" data-testid="mobile-nav-signup">Start Trial</Button>
                  </Link>
                </div>
              </SheetContent>
            </Sheet>

            <img src="/konnect-logo.png" alt="Konnect" className="h-6 sm:h-8 w-auto object-contain" />
            <nav className="hidden md:flex items-center gap-5 text-sm text-gray-medium">
              <a href="#pain" className="hover:text-text-primary transition-colors">Why Konnect</a>
              <a href="#features" className="hover:text-text-primary transition-colors">Platform</a>
              <a href="#enterprise" className="hover:text-text-primary transition-colors">Enterprise</a>
              <a href="#pricing" className="hover:text-text-primary transition-colors">Pricing</a>
              <Link to="/public/marketplace" className="hover:text-emerald-600 text-emerald-brand font-medium transition-colors" data-testid="marketplace-nav-link">Marketplace</Link>
            </nav>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-sm text-text-primary px-2 sm:px-4" data-testid="login-nav-btn">Sign In</Button>
            </Link>
            <Link to="/signup">
              <Button className="bg-emerald-brand hover:bg-emerald-hover text-sm px-3 sm:px-4" data-testid="signup-nav-btn">
                Start Trial
              </Button>
            </Link>
          </div>
        </div>
      </header>
      {/* Spacer — reserves the fixed-header height on desktop so the hero
          doesn't slide underneath. Hidden on mobile where the hero image
          intentionally fills behind the floating header. */}
      <div className="hidden lg:block h-16" aria-hidden="true" />

      {/* Hero — v2.5.926
          Desktop: IMAGE LEFT (square) · TEXT RIGHT, dead-center split.
          Mobile: IMAGE fills the ENTIRE viewport (100vh, portrait, edge-to-edge)
          INCLUDING behind the sticky header (negative top margin so the header
          floats over the photo). Text scrolls into view below the image. */}
      <section className="-mt-16 lg:mt-0 pt-0 sm:pt-8 lg:pt-10 pb-6 sm:pb-10 bg-gradient-to-b from-white via-emerald-brand/[0.03] to-off-white">
        <div className="max-w-none lg:max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-12 items-center">
          {/* ─── LEFT (desktop) / TOP (mobile): full-bleed 100vh visual hook ─── */}
          <div className="lg:pl-4 order-1">
            <BusinessCarousel />
          </div>

          {/* ─── RIGHT (desktop) / BELOW (mobile): the pitch content ─── */}
          <div className="text-center lg:text-left order-2 px-4 pt-8 lg:pt-0 lg:pr-4">
            <div className="flex justify-center lg:justify-start mb-2">
              <img src="/konnect-k-icon.png" alt="K" className="h-14 w-14 sm:h-16 sm:w-16 drop-shadow-md" data-testid="hero-k-icon" />
            </div>
            {/* Official tagline — sits directly under the K icon, above the beta badge */}
            <p className="text-emerald-brand font-heading font-semibold text-sm sm:text-base tracking-wide mb-3" data-testid="hero-tagline">
              {content?.hero?.tagline || 'Connecting Pieces Of The Business Puzzle'}
            </p>
            {/* Beta badge — Middle East & Southeast Asia */}
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full mb-3 sm:mb-3 border border-amber-200" data-testid="beta-badge">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <Sparkles className="h-3.5 w-3.5" />
              {content?.hero?.badge_text || 'Currently in private beta across Middle East & Southeast Asia'}
            </div>
            <h1 className="text-2xl sm:text-4xl lg:text-5xl xl:text-6xl font-heading font-bold text-text-primary mb-3 sm:mb-4 tracking-tight leading-[1.15]" data-testid="hero-headline">
              {heroTitle ? (
                heroTitle
              ) : (
                <>
                  {content?.hero?.title_line_1 || 'One Platform to Elevate'}<br className="hidden sm:block" />
                  <span className="text-emerald-brand">{content?.hero?.title_line_2 || 'Your Business Operation'}</span>
                </>
              )}
            </h1>
            {/* KONNECT acronym — sits right below the title so the brand story is instantly clear */}
            <p className="text-xs sm:text-sm text-gray-medium/80 mb-3 sm:mb-4 px-2 lg:px-0 leading-relaxed" data-testid="hero-acronym">
              {(content?.hero?.acronym_tagline || 'Knowledge-driven Operations · Networking · Next-level Engagement · Commerce & Technology')
                .split('·')
                .map((part, i, arr) => {
                  const words = part.trim().split(/(\s+)/);
                  return (
                    <span key={i}>
                      {words.map((w, j) => {
                        if (!w.trim()) return <span key={j}>{w}</span>;
                        if (!/^[A-Za-z]/.test(w)) return <span key={j}>{w}</span>;
                        return (
                          <span key={j}>
                            <strong className="text-text-primary font-semibold">{w.charAt(0)}</strong>{w.slice(1)}
                          </span>
                        );
                      })}
                      {i < arr.length - 1 && <span className="mx-1.5 text-gray-300">·</span>}
                    </span>
                  );
                })}
            </p>
            <p className="text-base sm:text-lg text-gray-medium mb-4 sm:mb-5 px-2 lg:px-0 leading-relaxed">
              {heroSubtitle || content?.hero?.subtitle || (
                <>Konnect unifies lead generation, sales, marketing, customer service, operations,
                and analytics into a single AI-powered platform — so you can focus on what matters most.</>
              )}
            </p>
            <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3 sm:gap-4">
              <Link to="/signup" className="w-full sm:w-auto">
                <Button size="lg" className="bg-emerald-brand hover:bg-emerald-hover px-6 sm:px-8 w-full sm:w-auto text-base" data-testid="hero-cta-btn">
                  {content?.hero?.primary_cta || 'Start Your Free 14-Day Trial'}
                  <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="gap-2 w-full sm:w-auto text-base" onClick={handleShowDemo} data-testid="watch-demo-btn">
                <Play className="h-4 w-4 sm:h-5 sm:w-5" />
                {content?.hero?.secondary_cta || 'Watch 3-Minute Demo'}
              </Button>
              <Link to="/tour/konnect" className="w-full sm:w-auto">
                <Button size="lg" variant="ghost" className="gap-2 w-full sm:w-auto text-base text-emerald-brand hover:text-emerald-hover hover:bg-emerald-50" data-testid="hero-cinematic-tour-btn">
                  <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
                  55-sec Cinematic Tour
                </Button>
              </Link>
            </div>
            <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-medium">
              {content?.hero?.trust_line || 'No credit card required. Full data migration included for Enterprise.'}
            </p>
          </div>
        </div>
      </section>

      {/* Real-time ROI Impact widget — visualizes what a typical Konnect subscriber saves */}
      <LiveROIWidget content={content} />

      {/* Interactive personal ROI calculator — pick industry + team size, see savings live */}
      <ROICalculatorWidget content={content} />

      {/* Honest stats strip — no more fake "10,000+ businesses" claims. */}
      <section className="py-8 sm:py-10 px-4 bg-charcoal" data-testid="stats-strip">
        <div className="max-w-5xl mx-auto">
          <p className="text-center text-xs uppercase tracking-widest text-gray-400 mb-5">What Konnect ships out of the box</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            {(content?.stats_strip || [
              { value: '89', label: 'Automated Modules', sublabel: 'One unified platform' },
              { value: '155', label: 'Currencies', sublabel: 'Global multi-tenant' },
              { value: '60+', label: 'Languages', sublabel: 'Localized end-to-end' },
              { value: '24/7', label: 'AI Operations', sublabel: 'Never off the clock' },
            ]).map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl sm:text-3xl font-heading font-bold text-white">{stat.value}</p>
                <p className="text-xs sm:text-sm text-gray-light mt-1">{stat.label}</p>
                {stat.sublabel && <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">{stat.sublabel}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beta partners strip — honest transparency about pilot status */}
      <section className="py-8 sm:py-10 px-4 bg-white border-b border-gray-100" data-testid="beta-partners-strip">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 mb-3">
            <MapPin className="h-4 w-4 text-emerald-brand" />
            <p className="text-xs sm:text-sm uppercase tracking-widest text-emerald-brand font-semibold">
              {content?.beta_partners_headline || 'Currently in private beta with select partners'}
            </p>
          </div>
          <p className="text-sm sm:text-base text-gray-medium max-w-2xl mx-auto mb-5 leading-relaxed">
            {content?.beta_partners_subline || "We're refining Konnect alongside SMBs and enterprise pilots across the Middle East and Southeast Asia. Interested in joining? Reach out — beta slots are limited."}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {(content?.beta_regions || ['Middle East', 'Southeast Asia']).map((region, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-brand/5 border border-emerald-brand/20 text-emerald-brand text-sm font-medium"
                data-testid={`beta-region-${i}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-brand animate-pulse" />
                {region}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points — 5 biggest real-world impacts. Every card also shows the hidden cost. */}
      <section id="pain" className="py-12 sm:py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-14">
            <p className="text-red-500 text-sm font-medium mb-2 uppercase tracking-wide">The Hidden Cost of Business as Usual</p>
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-3 sm:mb-4 px-2">
              These Losses Never Show Up on Your P&amp;L
            </h2>
            <p className="text-sm sm:text-base text-gray-medium max-w-2xl mx-auto px-2">
              Every business bleeds time, revenue, and talent to problems most owners never quantify. Here&apos;s what Konnect is built to eliminate:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {(content?.pain_points || painPoints).map((point, i) => {
              const Icon = PAIN_ICON_MAP[point.icon] || (point.icon?.render ? point.icon : AlertTriangle);
              return (
                <Card key={i} className="relative overflow-hidden group hover:shadow-xl transition-all duration-300 border-red-100 bg-gradient-to-br from-red-50/40 to-white" data-testid={`pain-card-${i}`}>
                  <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/[0.04] rounded-full -mr-8 -mt-8 group-hover:bg-red-500/[0.08] transition-colors" />
                  <CardContent className="relative p-5 sm:p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-red-500" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base sm:text-lg font-heading font-semibold text-text-primary mb-1 leading-snug">{point.title}</h3>
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-medium leading-relaxed mb-4">{point.desc}</p>
                    {point.impact_stat && (
                      <div className="flex items-center gap-2 pt-3 border-t border-red-100">
                        <TrendingDown className="h-4 w-4 text-red-500 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-semibold text-red-600" data-testid={`pain-stat-${i}`}>{point.impact_stat}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solution / Before & After */}
      <section className="py-12 sm:py-20 px-4 bg-emerald-brand/[0.03]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-14">
            <p className="text-emerald-brand text-sm font-medium mb-2 uppercase tracking-wide">The Konnect Difference</p>
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-3 sm:mb-4">
              From Fragmented to Unified
            </h2>
          </div>
          <div className="space-y-4 sm:space-y-5">
            {solutions.map((sol, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-border p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6">
                <div className="w-10 h-10 rounded-xl bg-emerald-brand/10 flex items-center justify-center flex-shrink-0">
                  <sol.icon className="h-5 w-5 text-emerald-brand" />
                </div>
                <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 flex-1">
                    <X className="h-4 w-4 text-red-400 flex-shrink-0" />
                    <span className="text-sm text-gray-medium line-through">{sol.before}</span>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-300 hidden sm:block flex-shrink-0" />
                  <div className="flex items-center gap-2 flex-1">
                    <Check className="h-4 w-4 text-emerald-brand flex-shrink-0" />
                    <span className="text-sm font-medium text-text-primary">{sol.after}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-12 sm:py-20 px-4 bg-off-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-14">
            <p className="text-emerald-brand text-sm font-medium mb-2 uppercase tracking-wide">One platform, every function</p>
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-3 sm:mb-4 px-2">
              Everything Your Business Needs — Nothing It Doesn&apos;t
            </h2>
            <p className="text-sm sm:text-base text-gray-medium max-w-2xl mx-auto px-2">
              From first lead to loyal customer, from operations to analytics — every business function, powered by AI, working together seamlessly.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="bg-white border-gray-border card-hover group">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-brand/10 flex items-center justify-center group-hover:bg-emerald-brand/20 transition-colors">
                      <feature.icon className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-brand" />
                    </div>
                    <span className="text-[10px] sm:text-xs font-medium text-emerald-brand bg-emerald-brand/10 px-2 py-0.5 rounded-full">{feature.stat}</span>
                  </div>
                  <h3 className="text-base sm:text-lg font-heading font-semibold text-text-primary mb-1 sm:mb-2">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-medium leading-relaxed">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Section */}
      <section id="enterprise" className="py-12 sm:py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-14">
            <p className="text-emerald-brand text-sm font-medium mb-2 uppercase tracking-wide">Built for Scale</p>
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-3 sm:mb-4 px-2">
              Enterprise-Grade for Banks, Franchises & Global Brands
            </h2>
            <p className="text-sm sm:text-base text-gray-medium max-w-2xl mx-auto px-2">
              Konnect handles the complexity of multi-country, multi-currency, multi-language operations. 
              We migrate your data, integrate your existing systems, and support your growth — globally.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {enterpriseFeatures.map((f, i) => (
              <Card key={i} className="bg-gray-50/50 border-gray-border">
                <CardContent className="p-5 sm:p-6">
                  <div className="w-10 h-10 rounded-xl bg-charcoal/5 flex items-center justify-center mb-3">
                    <f.icon className="h-5 w-5 text-charcoal" />
                  </div>
                  <h3 className="text-base font-heading font-semibold text-text-primary mb-1">{f.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-medium leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Integration logos */}
          <div className="mt-10 text-center">
            <p className="text-xs text-gray-medium uppercase tracking-wide mb-5 font-semibold">
              Integrates with your existing tools
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
              {integrationLogos.map(({ key, label, color }) => {
                const Icon = INTEGRATION_ICON_MAP[key];
                return (
                  <div
                    key={key}
                    className="group flex items-center gap-2.5 bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md rounded-xl px-3.5 py-2.5 transition-all duration-300 hover:-translate-y-0.5"
                    data-testid={`integration-logo-${key}`}
                    title={label}
                  >
                    {Icon && (
                      <Icon
                        className="h-5 w-5 sm:h-6 sm:w-6 shrink-0 transition-transform group-hover:scale-110"
                        style={{ color }}
                      />
                    )}
                    <span className="text-sm font-medium text-text-primary whitespace-nowrap">
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] sm:text-xs text-gray-400 mt-5 italic" data-testid="integration-disclaimer">
              Logos above are trademarks of their respective owners. Shown here to indicate supported integrations.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-20 px-4 bg-off-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 sm:mb-14">
            <p className="text-emerald-brand text-sm font-medium mb-2 uppercase tracking-wide">Simple as 1-2-3</p>
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-3 sm:mb-4">
              Go From Legacy to Automated in Days
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              { step: '1', title: 'Migrate & Connect', desc: 'We migrate your data from any system. Connect your existing tools — Stripe, Google, Meta, QuickBooks, and more.' },
              { step: '2', title: 'Configure & Customize', desc: 'Set your currency, timezone, and language. Configure roles, departments, and workflows that match your operation.' },
              { step: '3', title: 'Automate & Scale', desc: 'AI takes over repetitive tasks across every department. Smart alerts keep you informed. Scale to new markets effortlessly.' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-brand text-white flex items-center justify-center text-lg font-bold mx-auto mb-4">{item.step}</div>
                <h3 className="text-base sm:text-lg font-heading font-semibold text-text-primary mb-2">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-medium leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-12 sm:py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-14">
            <p className="text-emerald-brand text-sm font-medium mb-2 uppercase tracking-wide">Simple, transparent pricing</p>
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-3 sm:mb-4">
              Plans That Scale With Your Business
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {plans.map((plan, i) => {
              // Operator price/currency overrides — fall back to the default $-priced plan
              // when the operator hasn't customised that plan's price.
              const overridePrice = operatorPrices[plan.name];
              const displayPrice = overridePrice
                ? `${currencySymbol}${overridePrice}`
                : (operatorBrand && plan.price.startsWith('$')
                    ? plan.price.replace('$', currencySymbol)
                    : plan.price);
              return (
              <Card key={i} className={`relative bg-white ${plan.popular ? 'border-2 border-emerald-brand shadow-lg scale-[1.02]' : 'border-gray-border'}`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-brand text-white text-[10px] sm:text-xs font-medium px-3 py-0.5 rounded-full">Most Popular</div>}
                <CardContent className="p-5 sm:p-8">
                  <h3 className="text-lg sm:text-xl font-heading font-bold text-text-primary">{plan.name}</h3>
                  <p className="text-xs sm:text-sm text-gray-medium mt-1 mb-4">{plan.desc}</p>
                  <div className="flex items-baseline gap-1 mb-5 sm:mb-6">
                    <span className="text-3xl sm:text-4xl font-heading font-bold text-text-primary">{displayPrice}</span>
                    <span className="text-sm text-gray-medium">{plan.period}</span>
                  </div>
                  <Link to="/signup">
                    <Button className={`w-full mb-5 sm:mb-6 ${plan.popular ? 'bg-emerald-brand hover:bg-emerald-hover' : ''}`} variant={plan.popular ? 'default' : 'outline'}>
                      {plan.cta}
                    </Button>
                  </Link>
                  <ul className="space-y-2 sm:space-y-3">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs sm:text-sm text-gray-medium">
                        <Check className="h-4 w-4 text-emerald-brand flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-12 sm:py-20 px-4 bg-off-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-14">
            <p className="text-emerald-brand text-sm font-medium mb-2 uppercase tracking-wide">Voices from our beta cohort</p>
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-3 sm:mb-4">
              Business Leaders Who Have Been Exploring Konnect
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {testimonials.map((t, i) => (
              <Card key={i} className="bg-white border-gray-border">
                <CardContent className="p-5 sm:p-6">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <Star key={j} className="h-4 w-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm sm:text-base text-text-primary mb-4 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-brand/10 flex items-center justify-center">
                      <span className="text-emerald-brand font-bold text-sm">{t.name[0]}</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-text-primary">{t.name}</p>
                      <p className="text-xs text-gray-medium">{t.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach */}
      <section className="py-12 sm:py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Globe className="h-5 w-5 text-emerald-brand" />
            <p className="text-emerald-brand text-sm font-medium uppercase tracking-wide">Truly Global</p>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-heading font-bold text-text-primary mb-3 sm:mb-4">
            Operated Locally, Powered Globally
          </h2>
          <p className="text-sm sm:text-base text-gray-medium max-w-2xl mx-auto mb-6">
            155 currencies. 91 timezones. 60 languages. Konnect is available in multiple countries through certified local operators who understand 
            your market, culture, and regulations — backed by enterprise-grade global infrastructure.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-emerald-brand hover:bg-emerald-hover">
              Find Your Local Operator
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Investor Overview — appears just before Final CTA. Populated from CMS. */}
      {content?.investor?.enabled !== false && (
        <section id="investors" className="py-14 sm:py-20 px-4 bg-gradient-to-br from-slate-50 to-emerald-50/30 border-y border-emerald-100" data-testid="investor-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-brand/10 text-emerald-brand text-xs font-semibold mb-3">
                <Award className="h-3.5 w-3.5" />
                {content?.investor?.eyebrow || 'For Investors & Strategic Partners'}
              </span>
              <h2 className="text-2xl sm:text-4xl font-heading font-bold text-text-primary mb-3">
                {content?.investor?.headline || 'A Business Operating System for the Emerging Markets'}
              </h2>
              <p className="max-w-2xl mx-auto text-sm sm:text-base text-gray-medium leading-relaxed">
                {content?.investor?.subline || 'Konnect is a full-stack SaaS business operating system built by Kaharian Integrated Business Solutions. We are unifying the fragmented tools SMBs across the Middle East & Southeast Asia rely on today.'}
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {(content?.investor?.stats || [
                { value: '89+', label: 'Integrated modules' },
                { value: '400+', label: 'Cross-department automations' },
                { value: '$1.2T', label: 'SMB SaaS TAM by 2028' },
                { value: 'ME + SEA', label: 'Beta focus regions' },
              ]).map((s, i) => (
                <div key={i} className="bg-white rounded-xl border border-emerald-100 p-4 text-center shadow-sm" data-testid={`investor-stat-${i}`}>
                  <div className="text-2xl sm:text-3xl font-heading font-bold text-emerald-brand">{s.value}</div>
                  <div className="text-xs sm:text-sm text-gray-medium mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <Card className="bg-white/70 backdrop-blur border-emerald-100">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="h-5 w-5 text-emerald-brand" />
                    <h3 className="font-heading font-semibold text-lg">Why now</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-medium">
                    {(content?.investor?.why_now || [
                      'SMBs in ME & SEA are digitizing operations 3x faster post-2024',
                      'AI-native platforms are replacing legacy CRM/ERP suites',
                      'Regional SaaS penetration is <15% — massive greenfield',
                      'Konnect ships 89 modules from Day 1, not 5',
                    ]).map((p, i) => (
                      <li key={i} className="flex gap-2"><Check className="h-4 w-4 text-emerald-brand flex-shrink-0 mt-0.5" /><span>{p}</span></li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-white/70 backdrop-blur border-emerald-100">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="h-5 w-5 text-emerald-brand" />
                    <h3 className="font-heading font-semibold text-lg">The Konnect moat</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-medium">
                    {(content?.investor?.moat || [
                      'Cross-module automation engine (89 modules talk to each other)',
                      'AI-powered ROI-first onboarding — value visible in Week 1',
                      'Regional pricing + multi-currency + compliance out of the box',
                      'Built-in white-label mode for consulting-led sales',
                    ]).map((p, i) => (
                      <li key={i} className="flex gap-2"><Check className="h-4 w-4 text-emerald-brand flex-shrink-0 mt-0.5" /><span>{p}</span></li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Vending-operator product tour — auto-detects YouTube / Vimeo / Google Drive / direct MP4 via SmartVideo.
                Source priority (v2.6.1):
                  1. Media Library asset assigned to `landing_investor_video`
                  2. CMS field `content.investor.video_url` (Landing Page Editor)
                  3. Placeholder + super-admin hint. */}
            {(() => {
              const investorUrl = investorMedia?.url || content?.investor?.video_url;
              return investorUrl ? (
              <div className="mb-10">
                <div className="relative w-full max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-xl border border-emerald-100" data-testid="investor-video">
                  <div className="aspect-video bg-charcoal">
                    <SmartVideo
                      url={investorUrl}
                      title={investorMedia?.title || "Konnect Vending Operator Tour"}
                      autoPlay={false}
                      muted={false}
                      controls
                    />
                  </div>
                </div>
                <p className="text-center text-xs text-gray-medium mt-3">
                  {content?.investor?.video_caption || 'Watch a real vending operator run their fleet — machines, locations, DEX reconciliation, routing, commissions and Kiosk TV — all inside Konnect.'}
                </p>
              </div>
            ) : (
              <div className="mb-10 max-w-3xl mx-auto">
                <div className="relative aspect-video rounded-2xl overflow-hidden bg-gradient-to-br from-emerald-brand/10 via-white to-emerald-brand/5 border-2 border-dashed border-emerald-200 flex items-center justify-center" data-testid="investor-video-placeholder">
                  <div className="text-center px-6">
                    <Play className="h-14 w-14 text-emerald-brand mx-auto mb-3" />
                    <p className="text-sm font-semibold text-text-primary">Vending Operator Walk-Through — coming soon</p>
                    <p className="text-xs text-gray-medium mt-1 max-w-md mx-auto">Watch a live vending operator manage machines, DEX sync, routing, cash reconciliation and route-tech commissions inside Konnect — one dashboard, zero spreadsheets.</p>
                  </div>
                </div>
                <p className="text-center text-[10px] text-gray-400 mt-2">
                  Super admin: activate this slot from <strong>Content → Media Management → Upload Media</strong>, set <strong>Assign To = &quot;Landing Page - Investor Video (Vending Tour)&quot;</strong>, and paste a <strong>YouTube</strong>, <strong>Vimeo</strong>, <strong>Google Drive share link</strong>, or upload a direct <strong>.mp4</strong> file. Google Drive files must be set to &quot;Anyone with the link — Viewer&quot;.
                </p>
              </div>
            );
            })()}

            <div className="bg-charcoal rounded-2xl p-6 sm:p-8 text-center">
              <h3 className="text-white text-lg sm:text-2xl font-heading font-bold mb-2">
                {content?.investor?.cta_headline || 'Interested in an investor briefing?'}
              </h3>
              <p className="text-gray-light text-sm mb-5 max-w-xl mx-auto">
                {content?.investor?.cta_subline || 'We share a full pitch deck, financial model, product roadmap, and beta traction on request. Serious enquiries only.'}
              </p>
              <Button
                size="lg"
                className="bg-emerald-brand hover:bg-emerald-hover"
                onClick={() => setInvestorFormOpen(true)}
                data-testid="investor-contact-btn"
              >
                Request Investor Deck
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <p className="mt-3 text-xs text-gray-medium">
                Direct enquiries → we respond within 2 business days.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-14 sm:py-24 px-4 bg-charcoal">
        <div className="max-w-3xl mx-auto text-center">
          <Award className="h-10 w-10 sm:h-12 sm:w-12 text-emerald-brand mx-auto mb-4" />
          <h2 className="text-xl sm:text-3xl lg:text-4xl font-heading font-bold text-white mb-3 sm:mb-4 px-2">
            {content?.cta_final_headline?.split('.')?.[0] || 'Ready to see Konnect run your operations?'}
            {content?.cta_final_headline?.includes('.') && <><br className="hidden sm:block" /><span className="text-emerald-brand">{content.cta_final_headline.split('.').slice(1).join('.')}</span></>}
          </h2>
          <p className="text-sm sm:text-base text-gray-light mb-6 sm:mb-8 px-2 leading-relaxed">
            {content?.cta_final_subline || 'Start a free 14-day trial or book a personalized walkthrough with our team. Enterprise migration included.'}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link to="/signup">
              <Button size="lg" className="bg-emerald-brand hover:bg-emerald-hover px-6 sm:px-10 text-base">
                Start Your Free Trial Now
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-xs text-gray-medium">14-day free trial. No credit card required. Cancel anytime.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 bg-slate-dark">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                <img src="/konnect-logo-white.png" alt="Konnect" className="h-6 sm:h-8 w-auto object-contain" />
              </div>
              <p className="text-xs sm:text-sm text-gray-medium">
                Connecting Pieces of the Business Puzzle. Knowledge-driven Operations, Networking, Next-level Engagement, Commerce & Technology.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm sm:text-base mb-3 sm:mb-4">Platform</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-medium">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#enterprise" className="hover:text-white transition-colors">Enterprise</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#testimonials" className="hover:text-white transition-colors">Testimonials</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm sm:text-base mb-3 sm:mb-4">Company</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-medium">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm sm:text-base mb-3 sm:mb-4">Legal</h4>
              <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-medium">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 sm:pt-8 border-t border-gray-700 text-center text-xs sm:text-sm text-gray-medium">
            <p>&copy; 2026 Konnect by Kaharian Integrated Business Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Demo Video Dialog */}
      <Dialog open={showDemoVideo} onOpenChange={setShowDemoVideo}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden bg-black border-0">
          <div className="relative">
            <button onClick={() => setShowDemoVideo(false)} className="absolute top-3 right-3 z-10 bg-black/60 rounded-full p-1.5 text-white hover:bg-black/80">
              <X className="h-5 w-5" />
            </button>
            {demoVideoUrl ? (
              /* v2.5.917 — Unified video renderer. Detects Google Drive /
                 YouTube / Vimeo / direct MP4 and picks the right embed. */
              <SmartVideo
                url={demoVideoUrl}
                title="Konnect Demo"
                autoPlay
                muted={false}
                controls
                className="w-full aspect-video"
              />
            ) : (
              <div className="w-full aspect-video flex flex-col items-center justify-center text-center p-8">
                <Play className="h-16 w-16 text-gray-600 mb-4" />
                <p className="text-white text-lg font-semibold mb-2">Demo Video Coming Soon</p>
                <p className="text-gray-400 text-sm">Upload a video via Media Management and assign it to &ldquo;Landing Page - Hero Video&rdquo;</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;

// ─── Investor Enquiry modal ─────────────────────────────────────────────────
// Captures pitch-deck requests to /api/public/investor/enquiry.
// Saves to leads collection tagged 'investor', and (if Resend is configured)
// notifies the founder + sends auto-reply to the investor.
const InvestorEnquiryModal = ({ onClose }) => {
  const [form, setForm] = useState({
    name: '', email: '', company: '', role: '', ticket_size: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/investor/enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Submit failed');
      setDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="investor-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {done ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-emerald-brand" />
            </div>
            <h3 className="text-xl font-heading font-bold mb-2">Thank you</h3>
            <p className="text-sm text-gray-medium mb-6">
              Your enquiry has been received. Our team from Kaharian Integrated Business Solutions will be in touch within 2 business days with the full investor deck.
            </p>
            <Button onClick={onClose} className="bg-emerald-brand hover:bg-emerald-hover" data-testid="investor-modal-close-done">Close</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-lg font-heading font-bold text-text-primary">Request Investor Deck</h3>
                <p className="text-xs text-gray-medium mt-0.5">We respond within 2 business days.</p>
              </div>
              <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none" data-testid="investor-modal-close">&times;</button>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-medium">Full name *</label>
              <input required value={form.name} onChange={set('name')} className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-brand" data-testid="investor-name" placeholder="Your full name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-medium">Email *</label>
              <input required type="email" value={form.email} onChange={set('email')} className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-brand" data-testid="investor-email" placeholder="you@fund.com" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-medium">Company / Fund</label>
                <input value={form.company} onChange={set('company')} className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-brand" data-testid="investor-company" placeholder="Fund name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-medium">Role</label>
                <input value={form.role} onChange={set('role')} className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-brand" data-testid="investor-role" placeholder="Partner, Angel, etc." />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-medium">Ticket size</label>
              <select value={form.ticket_size} onChange={set('ticket_size')} className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-brand bg-white" data-testid="investor-ticket">
                <option value="">Prefer not to say</option>
                <option value="Under $100K">Under $100K (Angel)</option>
                <option value="$100K-$250K">$100K–$250K</option>
                <option value="$250K-$500K">$250K–$500K</option>
                <option value="$500K-$1M">$500K–$1M</option>
                <option value="$1M-$3M">$1M–$3M</option>
                <option value="$3M-$5M">$3M–$5M (Seed)</option>
                <option value="$5M+">$5M+ (Series A)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-medium">Message (optional)</label>
              <textarea rows={3} value={form.message} onChange={set('message')} className="w-full border border-gray-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-brand resize-none" data-testid="investor-message" placeholder="Anything you'd like to share about your fund or interest area." />
            </div>

            {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">{error}</p>}

            <Button type="submit" className="w-full bg-emerald-brand hover:bg-emerald-hover" disabled={submitting} data-testid="investor-submit">
              {submitting ? 'Submitting…' : 'Submit Enquiry'}
              {!submitting && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
            <p className="text-[10px] text-gray-400 text-center">By submitting you agree to be contacted by Kaharian Integrated Business Solutions about Konnect.</p>
          </form>
        )}
      </div>
    </div>
  );
};

// ─── Real-time ROI Impact widget ─────────────────────────────────────────────
// Sits directly below the hero. As soon as it scrolls into view, all four
// counters tick up from 0 to the target. Visually reinforces "each movement
// shows ROI in real time" — the whole page feels alive, not static marketing.
function LiveROIWidget({ content }) {
  const containerRef = useRef(null);
  const inView = useInView(containerRef);
  const roi = content?.live_roi || { hours_saved_per_month: 1200, value_created_usd: 42000, tasks_automated: 3169, modules_active: 89 };

  const hours = useAnimatedCount(roi.hours_saved_per_month, 1600, inView);
  const value = useAnimatedCount(roi.value_created_usd, 1800, inView);
  const tasks = useAnimatedCount(roi.tasks_automated, 2000, inView);
  const modules = useAnimatedCount(roi.modules_active, 1200, inView);

  // Sparkline data (12 months, growing curve) — pure SVG, no library needed
  const points = [8, 14, 22, 28, 38, 51, 65, 78, 88, 96, 108, 120];
  const w = 320, h = 60, pad = 4;
  const max = Math.max(...points);
  const pathD = points
    .map((v, i) => {
      const x = pad + (i * (w - 2 * pad)) / (points.length - 1);
      const y = h - pad - ((v / max) * (h - 2 * pad));
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <section ref={containerRef} className="py-10 sm:py-14 px-4 bg-white" data-testid="live-roi-widget">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 bg-emerald-brand/10 text-emerald-brand text-xs font-semibold px-3 py-1.5 rounded-full mb-3 uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-brand animate-pulse" />
            Live ROI Impact — a typical Konnect subscriber
          </div>
          <h2 className="text-xl sm:text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-2 px-2">
            Every module you activate<span className="text-emerald-brand"> pays you back</span>
          </h2>
          <p className="text-sm sm:text-base text-gray-medium max-w-2xl mx-auto px-2">
            Real-world benchmarks from our beta operators — updated in real time inside every subscriber&apos;s Efficiency &amp; ROI dashboard.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <LiveROICard
            icon={Clock}
            iconColor="text-blue-500"
            iconBg="bg-blue-50"
            value={hours.toLocaleString()}
            suffix=" hrs"
            label="Saved per month"
            testid="roi-hours"
          />
          <LiveROICard
            icon={DollarSign}
            iconColor="text-emerald-brand"
            iconBg="bg-emerald-brand/10"
            value={`$${(value / 1000).toFixed(1)}K`}
            suffix=""
            label="Productivity value created"
            testid="roi-value"
          />
          <LiveROICard
            icon={Zap}
            iconColor="text-amber-500"
            iconBg="bg-amber-50"
            value={tasks.toLocaleString()}
            suffix=""
            label="Tasks automated"
            testid="roi-tasks"
          />
          <LiveROICard
            icon={Layers}
            iconColor="text-purple-500"
            iconBg="bg-purple-50"
            value={modules.toString()}
            suffix=" / 89"
            label="Modules working for you"
            testid="roi-modules"
          />
        </div>

        {/* Compound growth curve — shows ROI accelerates as more modules activate */}
        <div className="bg-gradient-to-br from-emerald-brand/[0.03] to-white border border-emerald-brand/10 rounded-2xl p-5 sm:p-6" data-testid="roi-growth-chart">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-brand font-semibold mb-1">Compound ROI Growth</p>
              <p className="text-sm sm:text-base text-text-primary font-medium">Hours saved climb month-over-month as automation stacks</p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <TrendingUp className="h-4 w-4 text-emerald-brand" />
              <span className="font-semibold text-emerald-brand">+1400% over 12 months</span>
            </div>
          </div>
          <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16 sm:h-20" preserveAspectRatio="none">
            <defs>
              <linearGradient id="roiGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={`${pathD} L ${w - pad} ${h - pad} L ${pad} ${h - pad} Z`} fill="url(#roiGradient)" />
            <path d={pathD} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            {inView && points.map((v, i) => {
              const x = pad + (i * (w - 2 * pad)) / (points.length - 1);
              const y = h - pad - ((v / max) * (h - 2 * pad));
              return (
                <circle
                  key={i}
                  cx={x}
                  cy={y}
                  r="2"
                  fill="#fff"
                  stroke="#10b981"
                  strokeWidth="1.5"
                  style={{ opacity: 0, animation: `roi-dot-fade 0.4s ease-out ${i * 0.08}s forwards` }}
                />
              );
            })}
          </svg>
          <div className="flex justify-between text-[10px] sm:text-xs text-gray-400 mt-2">
            <span>Month 1</span>
            <span>Month 6</span>
            <span>Month 12</span>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes roi-dot-fade { to { opacity: 1; } }
      `}</style>
    </section>
  );
}

function LiveROICard({ icon: Icon, iconColor, iconBg, value, suffix, label, testid }) {
  return (
    <div className="bg-white border border-gray-border rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={testid}>
      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg ${iconBg} flex items-center justify-center mb-3`}>
        <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${iconColor}`} />
      </div>
      <p className="text-2xl sm:text-3xl font-heading font-bold text-text-primary tabular-nums leading-tight">
        {value}<span className="text-base sm:text-lg font-medium text-gray-medium">{suffix}</span>
      </p>
      <p className="text-xs sm:text-sm text-gray-medium mt-1 leading-tight">{label}</p>
    </div>
  );
}

// ─── Interactive ROI Calculator ─────────────────────────────────────────────
// Prospect enters team size + industry → instant personalized savings figure.
// Zero server round-trip: all math client-side once content is loaded.
// Data source: content.roi_calculator (from CMS defaults).
export function ROICalculatorWidget({ content }) {
  const cfg = content?.roi_calculator;
  const industries = cfg?.industries || [];
  const [teamSize, setTeamSize] = useState(25);
  const [industryKey, setIndustryKey] = useState('professional_services');
  const [hourlyRate, setHourlyRate] = useState(cfg?.hourly_rate_usd ?? 32);

  // Sync default hourly rate when content arrives
  useEffect(() => {
    if (cfg?.hourly_rate_usd) setHourlyRate(cfg.hourly_rate_usd);
  }, [cfg?.hourly_rate_usd]);

  const industry = industries.find((i) => i.key === industryKey) || industries[0];
  const baseHours = cfg?.hours_saved_per_employee ?? 42;
  const konnectCost = cfg?.konnect_monthly_cost_usd ?? 499;

  // Live calculations — recompute on every keystroke, no async, no debounce.
  const monthlyHours = Math.round(teamSize * baseHours * (industry?.multiplier || 1));
  const monthlySavings = Math.round(monthlyHours * hourlyRate);
  const annualSavings = monthlySavings * 12;
  const roiRatio = konnectCost > 0 ? Math.round(monthlySavings / konnectCost) : 0;
  const netMonthly = monthlySavings - konnectCost;

  // Smooth-animate the big numbers between changes
  const animatedMonthly = useAnimatedCount(monthlySavings, 600, `${industryKey}-${teamSize}-${hourlyRate}`);
  const animatedAnnual = useAnimatedCount(annualSavings, 800, `${industryKey}-${teamSize}-${hourlyRate}`);

  if (!industries.length) return null;

  return (
    <section className="py-12 sm:py-20 px-4 bg-gradient-to-b from-white to-emerald-brand/[0.04]" data-testid="roi-calculator-widget">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-emerald-brand/10 text-emerald-brand text-xs font-semibold px-3 py-1.5 rounded-full mb-3 uppercase tracking-wider">
            <Zap className="h-3.5 w-3.5" />
            Calculate Your ROI in Under 2 Seconds
          </div>
          <h2 className="text-xl sm:text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-2 sm:mb-3 px-2">
            How much would <span className="text-emerald-brand">your team</span> save with Konnect?
          </h2>
          <p className="text-sm sm:text-base text-gray-medium max-w-2xl mx-auto px-2">
            Pick your industry and team size — we&apos;ll show you the hours and dollars you&apos;re leaving on the table today, based on real benchmarks from our beta operators.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-4 sm:gap-6">
          {/* Inputs — left 2/5 */}
          <div className="lg:col-span-2 bg-white border border-gray-border rounded-2xl p-5 sm:p-6 shadow-sm">
            <div className="space-y-5">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-text-primary mb-2 uppercase tracking-wider">
                  Your Industry
                </label>
                <select
                  value={industryKey}
                  onChange={(e) => setIndustryKey(e.target.value)}
                  className="w-full border border-gray-border rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-brand"
                  data-testid="roi-calc-industry"
                >
                  {industries.map((ind) => (
                    <option key={ind.key} value={ind.key}>{ind.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-text-primary mb-2 uppercase tracking-wider">
                  Team Size
                  <span className="ml-2 text-emerald-brand font-bold text-base normal-case tracking-normal">
                    {teamSize} {teamSize === 1 ? 'person' : 'people'}
                  </span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="500"
                  value={teamSize}
                  onChange={(e) => setTeamSize(Number(e.target.value))}
                  className="w-full accent-emerald-brand"
                  data-testid="roi-calc-teamsize"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>1</span><span>50</span><span>150</span><span>300</span><span>500</span>
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-text-primary mb-2 uppercase tracking-wider">
                  Avg. Hourly Rate
                  <span className="ml-2 text-emerald-brand font-bold text-base normal-case tracking-normal">
                    ${hourlyRate}/hr
                  </span>
                </label>
                <input
                  type="range"
                  min="10"
                  max="150"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(Number(e.target.value))}
                  className="w-full accent-emerald-brand"
                  data-testid="roi-calc-hourly"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>$10</span><span>$40</span><span>$80</span><span>$120</span><span>$150</span>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-gray-100">
              <p className="text-[11px] uppercase tracking-wider text-gray-medium font-semibold mb-2">
                Top modules for {industry?.label?.split(' /')[0]}
              </p>
              <div className="flex flex-wrap gap-1.5" data-testid="roi-calc-top-modules">
                {(industry?.top_modules || []).map((m) => (
                  <span key={m} className="text-[11px] sm:text-xs bg-emerald-brand/10 text-emerald-brand px-2 py-1 rounded-full font-medium">
                    {m}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Results — right 3/5 */}
          <div className="lg:col-span-3 bg-charcoal rounded-2xl p-5 sm:p-8 text-white relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-emerald-brand/10 rounded-full blur-3xl" />
            <div className="relative">
              <p className="text-xs uppercase tracking-widest text-emerald-brand font-semibold mb-1">Your projected impact</p>
              <p className="text-sm text-gray-300 mb-6">Based on {teamSize} {teamSize === 1 ? 'person' : 'people'} in {industry?.label}</p>

              <div className="grid grid-cols-2 gap-4 mb-5">
                <div data-testid="roi-calc-monthly">
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white tabular-nums leading-none">
                    ${animatedMonthly.toLocaleString()}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-300 mt-1.5">Saved every month</p>
                </div>
                <div data-testid="roi-calc-annual">
                  <p className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-emerald-brand tabular-nums leading-none">
                    ${animatedAnnual.toLocaleString()}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-300 mt-1.5">Saved every year</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-5 pt-4 border-t border-white/10">
                <div>
                  <p className="text-lg sm:text-xl font-bold text-white tabular-nums">{monthlyHours.toLocaleString()}</p>
                  <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider">Hours/month</p>
                </div>
                <div>
                  <p className="text-lg sm:text-xl font-bold text-white tabular-nums">{roiRatio}×</p>
                  <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider">ROI multiple</p>
                </div>
                <div>
                  <p className="text-lg sm:text-xl font-bold text-white tabular-nums">${netMonthly.toLocaleString()}</p>
                  <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider">Net monthly</p>
                </div>
              </div>

              <div className="bg-emerald-brand/15 border border-emerald-brand/30 rounded-lg p-3.5 mb-5">
                <p className="text-xs sm:text-sm text-gray-100 leading-relaxed">
                  <span className="text-emerald-brand font-bold">For every $1 spent on Konnect,</span> your team gets back approximately{' '}
                  <span className="font-bold text-white">${roiRatio}</span> in productivity value.
                </p>
              </div>

              <Link to="/signup">
                <Button size="lg" className="w-full bg-emerald-brand hover:bg-emerald-hover text-base" data-testid="roi-calc-cta-btn">
                  Start Your Free 14-Day Trial
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>

              <EmailROIReportForm
                email=""
                industryKey={industryKey}
                teamSize={teamSize}
                hourlyRate={hourlyRate}
                monthlyDisplay={monthlySavings}
                annualDisplay={annualSavings}
              />

              <p className="text-[10px] sm:text-xs text-gray-400 text-center mt-3">
                * Estimates based on baseline benchmarks from beta operators. Actual results vary per business.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


// ─── Email-my-ROI-report capture form ───────────────────────────────────────
// Turns landing traffic into warm leads. Submitted emails become Lead records
// (source=roi_calculator) in the platform's Lead Gen module, plus a branded
// PDF report is emailed to the prospect with their industry benchmarks + top
// 10 automations + a 30-day activation checklist.
function EmailROIReportForm({ industryKey, teamSize, hourlyRate, monthlyDisplay, annualDisplay }) {
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [company, setCompany] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setStatus('error'); setErrorMsg('Please enter a valid email address.');
      return;
    }
    setStatus('submitting'); setErrorMsg('');
    try {
      await axios.post(`${API}/api/public/roi-report`, {
        email: email.trim(),
        first_name: firstName.trim() || null,
        company: company.trim() || null,
        industry_key: industryKey,
        team_size: teamSize,
        hourly_rate: hourlyRate,
      });
      setStatus('success');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrorMsg(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="mt-3 p-4 bg-emerald-brand/15 border border-emerald-brand/30 rounded-lg text-center" data-testid="roi-report-success">
        <Check className="h-6 w-6 text-emerald-brand mx-auto mb-2" />
        <p className="text-sm font-semibold text-white">Your ROI report is on its way!</p>
        <p className="text-xs text-gray-300 mt-1">
          Check {email} in a moment for your PDF with industry benchmarks and 30-day activation checklist.
        </p>
      </div>
    );
  }

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="mt-3 w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white text-sm font-medium py-2.5 rounded-lg border border-white/20 transition-colors"
        data-testid="roi-report-toggle-btn"
      >
        <Mail className="h-4 w-4" />
        Email me my personalized ROI report (PDF)
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="mt-3 space-y-2" data-testid="roi-report-form">
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          placeholder="Your name (optional)"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-emerald-brand"
          data-testid="roi-report-name"
        />
        <input
          type="text"
          placeholder="Company (optional)"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-emerald-brand"
          data-testid="roi-report-company"
        />
      </div>
      <div className="flex gap-2">
        <input
          type="email"
          placeholder="Your work email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="flex-1 px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-emerald-brand"
          data-testid="roi-report-email"
        />
        <Button
          type="submit"
          disabled={status === 'submitting'}
          className="bg-emerald-brand hover:bg-emerald-hover text-white px-4 shrink-0"
          data-testid="roi-report-submit-btn"
        >
          {status === 'submitting' ? 'Sending…' : 'Send PDF'}
        </Button>
      </div>
      {errorMsg && (
        <p className="text-xs text-red-300" data-testid="roi-report-error">{errorMsg}</p>
      )}
      <p className="text-[10px] text-gray-400 leading-relaxed">
        We&apos;ll email your ${monthlyDisplay.toLocaleString()}/mo &middot; ${annualDisplay.toLocaleString()}/yr projection.
        No spam — we may follow up once to help you get started.
      </p>
    </form>
  );
}

